import React, { FC, memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TaskStatusDictionary } from '@hkm/components/TaskManagement/relations/domain/interfaces';
import { Permission } from '@hkm/shared/permissions/enum/Permission';
import { usePermission } from '@hkm/shared/permissions/hooks/usePermission';
import { getLuhnAlghorithmValidationObject } from '@hkm/utils/getLuhnAlghorithmValidationObject';

import { TaskStatusRole } from '@ac/library-api';
import { AcBadge } from '@ac/mobile-components/dist/components/badge';
import {
  AcButton,
  AcButtonPattern
} from '@ac/mobile-components/dist/components/button';
import { AcButtonContent } from '@ac/mobile-components/dist/components/button-content';
import { AcDivider } from '@ac/mobile-components/dist/components/divider';
import { AcFlex } from '@ac/mobile-components/dist/components/flex';
import { AcIcon } from '@ac/mobile-components/dist/components/icon';
import {
  AcModal,
  AcModalBody,
  AcModalFooter,
  AcModalHeader,
  AcModalPattern
} from '@ac/mobile-components/dist/components/modal';
import { AcRadioButton } from '@ac/mobile-components/dist/components/radio-button';
import { AcText } from '@ac/mobile-components/dist/components/text';
import { AcTextarea } from '@ac/mobile-components/dist/components/textarea';
import {
  AlignItems,
  Color,
  FlexDirection,
  Icon,
  IconSize,
  JustifyContent,
  TextSize
} from '@ac/mobile-components/dist/enums';

import './../TaskModals.css';

export interface ComponentProps {
  statuses: TaskStatusDictionary[];
  isVisible: boolean;
  currentStatusId?: string;
  onChange: (statusId: string, comment?: string) => void;
  onClose: () => void;
}

const TaskChangeStatusModal: FC<ComponentProps> = (props: ComponentProps) => {
  const { t } = useTranslation();
  const hasPermissionToCancelTask = usePermission(Permission.TaskCancel);
  const [checkedStatus, setCheckedStatus] = useState<
    TaskStatusDictionary | undefined
  >();
  const [comment, setComment] = useState<string | undefined>();
  const [commentValidation, setCommentValidation] = useState<
    string | undefined
  >();

  const clearModal = useCallback(() => {
    setCommentValidation(undefined);
    setComment(undefined);
    setCheckedStatus(undefined);
  }, []);

  const closeModal = useCallback(() => {
    props.onClose();
    clearModal();
  }, [clearModal, props]);

  const onCommentChange = useCallback(
    (value?: string) => {
      setComment(value);
      value
        ? setCommentValidation(undefined)
        : setCommentValidation(t('VALIDATORS.IS_REQUIRED'));
    },
    [t]
  );

  const changeStatus = useCallback(() => {
    if (!checkedStatus) {
      return;
    }
    if (checkedStatus.isCommentMandatory && !comment) {
      return setCommentValidation(t('VALIDATORS.IS_REQUIRED'));
    }
    props.onChange(checkedStatus.id, comment);
    clearModal();
  }, [checkedStatus, clearModal, comment, props, t]);

  const availableStatuses = useMemo(() => {
    const activeStatuses = props.statuses.filter(({ isActive }) => isActive);
    const statusesWithCurrentExcluded = props.currentStatusId
      ? activeStatuses.filter(({ id }) => id !== props.currentStatusId)
      : activeStatuses;
    if (hasPermissionToCancelTask) {
      return statusesWithCurrentExcluded;
    }
    return statusesWithCurrentExcluded.filter(
      ({ role }) => role !== TaskStatusRole.Cancellation
    );
  }, [hasPermissionToCancelTask, props.currentStatusId, props.statuses]);

  return (
    <AcModal
      className="task-modal"
      isOpen={props.isVisible}
      onClose={closeModal}
      pattern={AcModalPattern.Drawer}
    >
      <AcModalHeader showCloseButton={true} className="task-modal-header">
        {t('TASK_MANAGEMENT.DASHBOARD.OPTIONS.CHANGE_STATUS')}
      </AcModalHeader>
      <AcModalBody>
        {(availableStatuses || []).map(status => {
          const setSelectedStatus = () => {
            setCheckedStatus(status);
            setComment(undefined);
            setCommentValidation(undefined);
          };

          return (
            <AcFlex
              key={status.id}
              className="full-width"
              direction={FlexDirection.column}
              grow={true}
            >
              <AcFlex
                className="full-width"
                alignItems={AlignItems.center}
                onClick={setSelectedStatus}
              >
                <AcRadioButton checked={checkedStatus?.id === status.id} />
                <AcBadge
                  fit
                  badgeText={status.code}
                  backgroundColor={status.color || '000'}
                />
                <AcText className="ac-spacing-left-sm">
                  {status.displayName}
                </AcText>
              </AcFlex>
              <AcDivider />
            </AcFlex>
          );
        })}
      </AcModalBody>
      <AcModalFooter className="task-modal-footer">
        <AcFlex
          direction={FlexDirection.column}
          grow={true}
          justifyContent={JustifyContent.center}
          alignItems={AlignItems.center}
        >
          {checkedStatus?.isCommentMandatory && (
            <AcFlex
              className="full-width ac-spacing-top-xs ac-spacing-bottom-md"
              direction={FlexDirection.column}
              grow={true}
            >
              <AcTextarea
                className="comment-field"
                required={true}
                value={comment}
                placeholder={t('GLOBAL.FILL')}
                label={t('TASK_MANAGEMENT.DASHBOARD.ADDITIONAL_INFO.COMMENT')}
                maxlength={200}
                validation={commentValidation}
                onChange={onCommentChange}
              />
              {getLuhnAlghorithmValidationObject(comment) && (
                <>
                  <AcText size={TextSize.Main3} className="ac-spacing-left-lg">
                    <AcIcon
                      className="ac-spacing-right-sm"
                      icon={Icon.Information}
                      color={Color.InformationDark}
                      size={IconSize.Tiny}
                    />
                    {getLuhnAlghorithmValidationObject(comment)}
                  </AcText>
                </>
              )}
              <AcText size={TextSize.Main3} className="ac-spacing-left-lg">
                {t('GLOBAL.REQUIRED_FIELDS')}
              </AcText>
            </AcFlex>
          )}
          <AcFlex
            className="full-width footer-buttons"
            direction={FlexDirection.column}
            grow={true}
          >
            <AcButton className="full-width" onClick={changeStatus}>
              <AcButtonContent text={t('GLOBAL.CHANGE')} />
            </AcButton>
            <AcButton
              pattern={AcButtonPattern.Tertiary}
              className="full-width"
              onClick={closeModal}
            >
              <AcButtonContent icon={Icon.Cancel} text={t('GLOBAL.CANCEL')} />
            </AcButton>
          </AcFlex>
        </AcFlex>
      </AcModalFooter>
    </AcModal>
  );
};

export default memo(TaskChangeStatusModal);



// WEBPACK FOOTER //
// ./src/components/TaskManagement/shared/components/Modals/TaskChangeStatusModal/TaskChangeStatusModal.tsx